// hooks/useGetPrograms.js
import { useState, useEffect } from 'react';
import { programService } from '../Services/programService';
import { useDispatch } from 'react-redux';
import { setSuccessSnack } from '../Reducers/snackSuccess';
import { handleApiError } from '../Utils/ApiErrorHandler';

const useGetPrograms = (role) => {
  const dispatch = useDispatch();

  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const createProgram = async (values) => {
    try {
      setCreating(true);
      const data = await programService.createProgram(values);
      
      if (data.status === 'OK') {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: `Created program successfully with id ${data.id}`
        }));
        await fetchPrograms(); // Refresh list
        return true;
      }
      return false;
    } catch (error) {
      handleApiError(error, dispatch, 'create program');
      return false;
    } finally {
      setCreating(false);
    }
  };
  
  const updateProgram = async (values) => {
    try {
      setUpdating(true);
      const data = await programService.updateProgram(values);
      
      if (data.status === 'Updated') {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: 'Updated successfully'
        }));
        await fetchPrograms(); // Refresh list
        return true;
      }
      return false;
    } catch (error) {
      handleApiError(error, dispatch, 'update program');
      return false;
    } finally {
      setUpdating(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const data = await programService.getPrograms();
      setPrograms(data);
      return data;
    } catch (error) {
      handleApiError(error, dispatch, 'get programs');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteProgram = async (id) => {
    if (!id) return false;

    try {
      setDeleting(true);
      const data = await programService.deleteProgram(id);
      
      if (data.status === 'Removed') {
        dispatch(setSuccessSnack({
          title: 'Success', 
          body: 'Removed program'
        }));
        await fetchPrograms(); // Refresh list
        return true;
      }
      return false;
    } catch (error) {
      handleApiError(error, dispatch, 'delete program');
      return false;
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  return {
    programs,
    loading,
    deleting,
    refetch: fetchPrograms,
    deleteProgram,
    createProgram,
    updateProgram
  };
};

export default useGetPrograms;